import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { getApiErrorMessage } from 'utils';

/**
 * @description API hatasını kullanıcıya gösterilecek metne çevirir. Öncelik sırası:
 * 1. **`403` → `apiError.forbidden`** (yetki hatası) — bu statüde BE gövdesi API Platform'un lokalize
 *    edilmemiş sabit `"Access Denied."` metnidir, kullanıcıya bir şey anlatmaz. Yetki hatası ürün dilinde
 *    açıklanır: kullanıcı "sistem hatası" değil "bu işlemi yapamıyorum" bilgisini alır.
 * 2. BE gövdesindeki iş kuralı mesajı (`hydra:description` / `violations` / `detail`) — `422` doğrulama
 *    ve iş kuralı hatalarının gerçek metni buradan gelir, ezilmemeli.
 * 3. `article.form.fetchError` generic metni (hata kodu enterpolasyonlu).
 *
 * NEDEN: Hata dalları bugün her çağrı yerinde ayrı ayrı yazılıyor ve çoğu BE gövdesini hiç okumuyor
 * (`error.status`'ü generic metne basıyor) ya da RTK Query hata objesinde bulunmayan `error.message`
 * alanını okuyup mesajı sessizce yutuyor. Bu hook o kararı tek yere toplar; çağrı yeri
 * `dispatch(fetchError(getErrorMessage(error)))` yazar, öncelik sırasını bilmek zorunda kalmaz.
 *
 * @returns {(error: unknown) => string} Hata objesini gösterilebilir metne çeviren, referansı sabit fonksiyon.
 *
 * @example
 * const getErrorMessage = useApiErrorMessage();
 * const { error } = await mutate(body);
 * if (error) return dispatch(fetchError(getErrorMessage(error)));
 */
export const useApiErrorMessage = () => {
  const { formatMessage } = useIntl();

  return useCallback(
    (error) => {
      const status = error?.status ?? error?.originalStatus;

      if (status === 403) return formatMessage({ id: 'apiError.forbidden' });

      return getApiErrorMessage(error) ?? formatMessage({ id: 'article.form.fetchError' }, { error: status ?? '-' });
    },
    [formatMessage]
  );
};
